/**
 * Reviews carousel for [data-carousel]: one infinite, transform-driven
 * track for every mode. Cards are recycled end-to-end as the track
 * moves, so the loop never runs out in either direction.
 *
 * - Desktop (hover, motion OK): slow ambient drift; hovering or focusing
 *   pauses it; the side arrows glide one card either way (they work
 *   while paused, so anyone can page by hand).
 * - Desktop with reduced motion: no drift; the arrows jump one card
 *   instantly. Still loops forever.
 * - Touch: no drift, arrows are hidden by CSS; the strip starts with a
 *   card centered (neighbors peeking in from the faded edges) and is
 *   dragged left/right, snapping to the nearest card on release.
 * - No JS: the viewport stays a native scroller and the arrows hide.
 */
const prefersReducedMotion = window.matchMedia(
  '(prefers-reduced-motion: reduce)'
).matches;
// Touch mode by capability OR viewport: some devices report themselves
// as hover-capable, so a phone-sized screen counts as touch regardless
// (matches the CSS breakpoint that hides the arrows).
const touchMode =
  window.matchMedia('(hover: none), (pointer: coarse)').matches ||
  window.innerWidth <= 832;

const SPEED = 24; // ambient drift, px per second
const GLIDE = 520; // arrow-press / snap glide, px per second

const carousels = document.querySelectorAll('[data-carousel]');
if (carousels.length > 0) {
  document.documentElement.classList.add('js-carousel');
}

carousels.forEach((carousel) => {
  const viewport = carousel.querySelector('[data-carousel-viewport]');
  const track = carousel.querySelector('[data-carousel-track]');
  if (!viewport || !track || track.children.length < 3) return;

  const prevBtn = carousel.querySelector('[data-carousel-prev]');
  const nextBtn = carousel.querySelector('[data-carousel-next]');

  viewport.classList.add('is-marquee');

  const gapOf = () =>
    parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
  const cardStep = () =>
    track.children[0].getBoundingClientRect().width + gapOf();

  let offset = 0;
  let paused = false;
  let last = null;
  let pending = 0; // px still owed to arrow presses / snap (+fwd, -back)
  let dragging = false;

  const drift = !touchMode && !prefersReducedMotion;

  if (drift) {
    carousel.addEventListener('mouseenter', () => { paused = true; });
    carousel.addEventListener('mouseleave', () => { paused = false; });
    carousel.addEventListener('focusin', () => { paused = true; });
    carousel.addEventListener('focusout', () => { paused = false; });
  }

  if (prevBtn) prevBtn.addEventListener('click', () => { pending -= cardStep(); });
  if (nextBtn) nextBtn.addEventListener('click', () => { pending += cardStep(); });

  // Offset that puts the card at `index` in the horizontal center.
  const centerOffsetOf = (index) => {
    const card = track.children[index];
    return card.offsetLeft - (viewport.clientWidth - card.getBoundingClientRect().width) / 2;
  };

  // Touch: drag the track directly. A short flick (30px) is enough to
  // commit to the next/previous card; releases snap a card to center.
  if (touchMode) {
    let lastX = 0;
    let dragDelta = 0; // total horizontal drag this gesture (recycle-safe)
    let interacted = false;
    viewport.addEventListener('pointerdown', (event) => {
      dragging = true;
      interacted = true;
      lastX = event.clientX;
      dragDelta = 0;
      pending = 0;
      viewport.setPointerCapture(event.pointerId);
    });
    viewport.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const step = lastX - event.clientX;
      offset += step;
      dragDelta += step;
      lastX = event.clientX;
    });
    const release = () => {
      if (!dragging) return;
      dragging = false;
      // Directional snap: a flick past 30px moves at least one card in
      // that direction; smaller movements settle on the nearest card.
      let target = null;
      let bestDist = Infinity;
      for (let i = 0; i < track.children.length; i += 1) {
        const co = centerOffsetOf(i);
        const d = co - offset;
        if (dragDelta > 30 && d < -1) continue; // forward flick: only cards ahead
        if (dragDelta < -30 && d > 1) continue; // backward flick: only cards behind
        if (Math.abs(d) < bestDist) { bestDist = Math.abs(d); target = co; }
      }
      if (target === null) {
        // No candidate in the flick direction (shouldn't happen with a
        // recycled loop) — fall back to the nearest card.
        for (let i = 0; i < track.children.length; i += 1) {
          const d = Math.abs(centerOffsetOf(i) - offset);
          if (d < bestDist) { bestDist = d; target = centerOffsetOf(i); }
        }
      }
      pending += target - offset;
    };
    viewport.addEventListener('pointerup', release);
    viewport.addEventListener('pointercancel', release);

    // Start with the second card centered — one neighbor peeks in from
    // each faded edge, signaling the strip scrolls. Re-run once images
    // have settled layout.
    const centerStart = () => { offset = centerOffsetOf(1); };
    centerStart();
    window.addEventListener('load', centerStart, { once: true });
    // Re-center after viewport changes (orientation flip, late resize)
    // until the visitor has interacted with the strip.
    window.addEventListener('resize', () => {
      if (!interacted && !dragging) centerStart();
    });
  }

  const tick = (now) => {
    if (last === null) last = now;
    // Clamp dt so a backgrounded tab does not fast-forward on return.
    const dt = Math.min(now - last, 100);
    last = now;

    if (!dragging) {
      let delta = 0;
      if (drift && !paused) delta += (SPEED * dt) / 1000;
      if (pending !== 0) {
        // Reduced motion: consume the whole press at once (instant jump).
        const glide = prefersReducedMotion ? Math.abs(pending) : (GLIDE * dt) / 1000;
        const step = Math.sign(pending) * Math.min(Math.abs(pending), glide);
        delta += step;
        pending -= step;
      }
      offset += delta;
    }

    // Recycle cards leaving on the left... (zero-width guard: never spin
    // if layout hasn't produced card sizes yet.)
    while (offset > 0) {
      const width = cardStep();
      if (width <= 0 || offset < width) break;
      track.appendChild(track.children[0]);
      offset -= width;
    }
    // ...and cards re-entering from the left when moving backwards.
    while (offset < 0) {
      const lastChild = track.children[track.children.length - 1];
      const width = lastChild.getBoundingClientRect().width + gapOf();
      if (width <= 0) { offset = 0; break; }
      track.insertBefore(lastChild, track.children[0]);
      offset += width;
    }
    track.style.transform = `translateX(${-offset}px)`;

    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
});
